/**
 * Modal picker that replaces SkillPromptSlots' hardcoded SKILL_TEMPLATES.
 * Reads from Convex `skills.list` (D3), filters by name + scope, and
 * emits the picked skill back through `onPick` as a SkillPrompt-shaped
 * object so the existing Prompt Lab redux flow is untouched.
 *
 * Win95 aesthetic via Tailwind to match the surrounding `SkillPromptSlots`
 * styling; inline style only for the title-bar gradient.
 */
import { useMemo, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import type { SkillPrompt } from '@repancy/core';
import { useOptionalTenantBranding } from '../../lib/tenant/TenantBrandingContext';

type Scope = 'firm' | 'client';

interface PickerSkill {
  _id: string;
  name: string;
  description: string;
  scope: Scope;
  currentVersion: number;
}

interface SkillPickerProps {
  scope?: Scope;
  clientId?: string;
  onPick: (
    prompt: SkillPrompt,
    meta: { skillId: string; skillVersionNumber: number },
  ) => void;
  onCancel: () => void;
}

export function SkillPicker({ scope, clientId, onPick, onCancel }: SkillPickerProps) {
  // F16.F.3 — when mounted inside a `/c/${slug}` portal, prefer the
  // slug-scoped query so the picker only shows Skills synced into that
  // firm's Firm-Skills library. Outside a portal we fall back to the
  // global `list` query.
  const portal = useOptionalTenantBranding();
  const portalSlug = portal?.slug ?? null;
  const liveSkillsBySlug = useQuery(
    api.skills.listForSlug,
    portalSlug !== null ? { clientSlug: portalSlug } : 'skip',
  );
  const liveSkills = useQuery(
    api.skills.list,
    portalSlug !== null
      ? 'skip'
      : {
          ...(scope ? { scope } : {}),
          ...(clientId ? { clientId: clientId as never } : {}),
        },
  );
  const liveSource = portalSlug !== null ? liveSkillsBySlug : liveSkills;
  const skills: PickerSkill[] | undefined = liveSource?.map((s) => ({
    _id: String(s._id),
    name: s.name,
    description: s.description,
    scope: s.scope,
    currentVersion: s.currentVersion,
  }));
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<Scope | 'all'>(scope ?? 'all');

  const filtered = useMemo(() => {
    if (!skills) return [] as NonNullable<typeof skills>;
    const needle = search.trim().toLowerCase();
    return skills.filter((s) => {
      if (scopeFilter !== 'all' && s.scope !== scopeFilter) return false;
      if (!needle) return true;
      return (
        s.name.toLowerCase().includes(needle) ||
        s.description.toLowerCase().includes(needle)
      );
    });
  }, [skills, search, scopeFilter]);

  let skillIdCounter = 0;
  const nextSkillPromptId = (): string => {
    skillIdCounter += 1;
    return `sp-${Date.now()}-${skillIdCounter}`;
  };

  return (
    <div
      onClick={onCancel}
      className="fixed inset-0 flex items-center justify-center bg-black/30 z-[10000]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[420px] bg-[#ece9d8] shadow-[2px_2px_0_#000]"
        style={{
          borderStyle: 'solid',
          borderWidth: 2,
        }}
        data-testid={testIds.skills.pickerRoot}
      >
        <div
          className="flex items-center justify-between font-bold text-[12px] text-white px-1.5 py-[3px]"
          style={{
            background: 'linear-gradient(90deg, var(--xp-selection), #1a4ab8)',
            fontFamily: "var(--xp-font-body)",
          }}
        >
          <span>Pick a Skill</span>
          <button
            onClick={onCancel}
            className="bg-[#ece9d8] border-2 border-solid border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-[#808080] border-r-[#808080] text-black font-bold text-[12px] leading-none px-1 cursor-pointer"
          >
            X
          </button>
        </div>

        <div className="p-2">
          <div className="flex gap-1 mb-2">
            <input
              type="text"
              placeholder="Filter…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid={testIds.skills.pickerSearch}
              className="flex-1 text-[11px] px-1 py-0.5 bg-white text-black border-2 border-solid border-t-[#808080] border-l-[#808080] border-b-[#dfdfdf] border-r-[#dfdfdf]"
              style={{
                fontFamily: "var(--xp-font-body)",
              }}
            />
            <button
              data-testid={testIds.skills.pickerScopeFirm}
              onClick={() => setScopeFilter(scopeFilter === 'firm' ? 'all' : 'firm')}
              className={`text-[11px] px-2 py-0.5 cursor-pointer text-black ${
                scopeFilter === 'firm' ? 'bg-[#a0a0a0]' : 'bg-[#ece9d8]'
              }`}
              style={{
                borderStyle: 'solid',
                borderWidth: 2,
              }}
            >
              Firm
            </button>
            <button
              data-testid={testIds.skills.pickerScopeClient}
              onClick={() => setScopeFilter(scopeFilter === 'client' ? 'all' : 'client')}
              className={`text-[11px] px-2 py-0.5 cursor-pointer text-black ${
                scopeFilter === 'client' ? 'bg-[#a0a0a0]' : 'bg-[#ece9d8]'
              }`}
              style={{
                borderStyle: 'solid',
                borderWidth: 2,
              }}
            >
              Client
            </button>
          </div>

          {skills === undefined && (
            <div className="text-[11px] text-gray-800 italic text-center py-3">
              Loading skills…
            </div>
          )}
          {skills && filtered.length === 0 && (
            <div className="text-[11px] text-gray-800 italic text-center py-3">
              No skills match. Use "Save as Skill…" on a working prompt to seed
              the library.
            </div>
          )}
          <div className="max-h-[320px] overflow-y-auto">
            {filtered.map((s) => (
              <button
                key={s._id}
                data-testid={testIds.skills.pickerItem(s._id)}
                onClick={() => {
                  const sp: SkillPrompt = {
                    id: nextSkillPromptId(),
                    name: s.name,
                    content: '',
                    weight: 1,
                    enabled: true,
                  };
                  onPick(sp, {
                    skillId: String(s._id),
                    skillVersionNumber: s.currentVersion,
                  });
                }}
                className="block w-full text-left mb-1 px-2 py-1 cursor-pointer bg-[#ece9d8] text-black"
                style={{
                  borderStyle: 'solid',
                  borderWidth: 2,
                  fontFamily: "var(--xp-font-body)",
                }}
              >
                <div className="font-bold text-[11px]">{s.name}</div>
                <div className="text-[10px] text-gray-800 mt-0.5">
                  {s.scope === 'firm' ? 'Firm' : 'Client'} · v{s.currentVersion}
                </div>
                <div className="text-[10px] text-black mt-0.5 line-clamp-2">
                  {s.description}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
